<template>
  <v-dialog v-model="dialogModel" max-width="700px" content-class="employee-dialog-wrapper">
    <v-card class="employee-card">
      <v-card-title class="employee-dialog-header">
        <v-icon color="white" class="mr-2">
          {{ editingEmployee ? 'mdi-account-edit' : 'mdi-account-plus' }}
        </v-icon>
        {{ editingEmployee ? '사원 정보 수정' : '신규 사원 등록' }}
      </v-card-title>
      
      <v-card-text class="employee-dialog-content">
        <v-form ref="employeeForm" v-model="formValid">
          <div class="form-section">
            <h4 class="section-title">기본 정보</h4>
            
            <div class="compact-field">
              <label class="compact-label">
                이름
                <span class="required-text">* 필수입력</span>
              </label>
              <input
                v-model="formData.emp_name"
                type="text"
                class="compact-input"
                placeholder="이름을 입력하세요"
                maxlength="20"
                @input="onNameInput"
              />
              <div class="field-hint">* 한글만 입력할 수 있습니다.</div>
            </div>
            
            <div class="compact-field">
              <label class="compact-label">
                생년월일
                <span class="required-text">* 필수입력</span>
              </label>
              <input
                v-model="formData.emp_birth"
                type="text"
                class="compact-input"
                placeholder="YYYYMMDD (예: 19900101)"
                maxlength="8"
                @input="onBirthInput"
              />
              <div class="field-hint">* 8자리 숫자만 입력하세요. 예: 19950729</div>
              <div v-if="formData.emp_birth && !isBirthValid" class="field-error">유효한 생년월일을 입력하세요.</div>
            </div>
            
            <div class="compact-field">
              <label class="compact-label">주민등록번호</label>
              <div class="birth-number-input-group">
                <input
                  v-model="birthNumberFront"
                  type="text"
                  class="compact-input birth-front"
                  maxlength="6"
                  placeholder="앞자리 6자리"
                  @input="onBirthNumberFrontInput"
                />
                <span class="birth-separator">-</span>
                <input
                  v-model="birthNumberBack"
                  type="text"
                  class="compact-input birth-back"
                  maxlength="7"
                  placeholder="뒷자리 7자리"
                  @input="onBirthNumberBackInput"
                />
              </div>
              <div class="field-hint">
                * 선택 사항입니다.
                <v-btn
                  v-if="formData.emp_birth"
                  size="x-small"
                  variant="text"
                  color="#1e3a8a"
                  class="ml-1"
                  @click="fillBirthFrontFromBirth"
                >
                  생년월일에서 앞자리 채우기
                </v-btn>
              </div>
            </div>
            
            <div class="compact-field">
              <label class="compact-label">
                직종
                <span class="required-text">* 필수입력</span>
              </label>
              <v-select
                v-model="formData.emp_job"
                :items="jobOptions"
                variant="outlined"
                density="compact"
                placeholder="직종을 선택하세요"
                hide-details
                :menu-props="{ zIndex: 9999 }"
              ></v-select>
            </div>
          </div>

          <div class="form-section">
            <h4 class="section-title">연락처 정보</h4>
            
            <div class="compact-field">
              <label class="compact-label">휴대폰번호</label>
              <input
                v-model="formData.emp_phone"
                type="text"
                class="compact-input"
                placeholder="010-1234-5678"
                maxlength="13"
                @input="onPhoneInput"
              />
            </div>
            
            <div class="compact-field">
              <label class="compact-label">이메일</label>
              <input
                v-model="formData.emp_email"
                type="email"
                class="compact-input"
                placeholder="이메일을 입력하세요"
                maxlength="50"
              />
            </div>
          </div>

          <div class="form-section">
            <h4 class="section-title">주소 정보</h4>
            
            <div class="compact-field">
              <label class="compact-label">주소</label>
              <div class="address-input-group">
                <input
                  v-model="formData.emp_addr"
                  type="text"
                  class="compact-input"
                  placeholder="주소 검색 버튼을 클릭하세요"
                  readonly
                  @click="openAddressModal"
                />
                <button
                  type="button"
                  class="address-search-btn"
                  @click="openAddressModal"
                >
                  주소검색
                </button>
              </div>
            </div>
            
            <div class="compact-field">
              <label class="compact-label">상세주소</label>
              <input
                v-model="formData.emp_addr_detail"
                type="text"
                class="compact-input"
                placeholder="상세주소를 입력하세요"
                maxlength="50"
              />
            </div>
          </div>

          <div class="form-section">
            <h4 class="section-title">재직 정보</h4>
            
            <div class="compact-field">
              <label class="compact-label">
                입사일
                <span class="required-text">* 필수입력</span>
              </label>
              <input
                v-model="formData.emp_in_date"
                type="date"
                class="compact-input"
              />
            </div>
            
            <div class="compact-field">
              <label class="compact-label">퇴사일</label>
              <input
                v-model="formData.emp_out_date"
                type="date"
                class="compact-input"
              />
            </div>
          </div>
        </v-form>
      </v-card-text>
      
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          variant="text"
          @click="closeDialog"
          :disabled="isSaving"
        >
          취소
        </v-btn>
        <v-btn
          color="#1e3a8a"
          variant="flat"
          @click="saveEmployee"
          :loading="isSaving"
          :disabled="!isFormValid"
        >
          <v-icon class="mr-1">mdi-content-save</v-icon>
          저장
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- 주소 검색 모달 -->
    <v-dialog v-model="addressDialog" max-width="560" content-class="address-dialog-wrapper">
      <v-card rounded="lg" class="address-card">
        <v-card-title class="text-subtitle-1 font-weight-bold">주소 검색</v-card-title>
        <v-card-text style="height: 420px; position: relative;">
          <div v-if="addressLoading" class="loading-overlay">
            <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
            <div class="loading-text mt-3">주소 검색을 불러오는 중...</div>
          </div>
          <div ref="postcodeContainer" style="width:100%; height:100%;" v-show="!addressLoading"></div>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn variant="text" @click="addressDialog = false" :disabled="addressLoading">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';

const store = useStore();
const user = computed(() => store.state.user);

interface Props {
  modelValue: boolean;
  employee?: any;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'saved'): void;
}>();

// 상태
const formValid = ref(false);
const employeeForm = ref();
const isSaving = ref(false);
const addressDialog = ref(false);
const addressLoading = ref(false);
const postcodeContainer = ref<HTMLDivElement | null>(null);
let daumScriptLoading: Promise<void> | null = null;

// 폼 데이터
const formData = ref({
  emp_code: '',
  m_sisul_code: '',
  emp_name: '',
  emp_birth: '',
  emp_birth_number: '',
  emp_job: '',
  emp_in_date: '',
  emp_out_date: '',
  emp_phone: '',
  emp_email: '',
  emp_addr: '',
  emp_addr_detail: ''
});

// 주민등록번호 입력 상태 (UI용)
const birthNumberFront = ref('');
const birthNumberBack = ref('');

// 생년월일에서 주민등록번호 앞자리 추출 (YYYYMMDD → YYMMDD)
const extractBirthFront = (birth: string): string => {
  if (!birth) return '';
  const digitsOnly = birth.replace(/[^\d]/g, '');
  if (digitsOnly.length === 8) {
    return digitsOnly.substring(2, 8);
  }
  return '';
};

// 생년월일에서 주민등록번호 앞자리 채우기
const fillBirthFrontFromBirth = () => {
  const front = extractBirthFront(formData.value.emp_birth);
  if (front) {
    birthNumberFront.value = front;
  }
};

// 옵션
const jobOptions = ['센터장', '사회복지사', '요양보호사', '간호사', '간호조무사', '물리치료사', '작업치료사', '기타'];

// computed
const dialogModel = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const editingEmployee = computed(() => props.employee);

const isValidBirthDate = (value: string) => {
  if (!/^\d{8}$/.test(value)) return false;
  const year = parseInt(value.slice(0, 4), 10);
  const month = parseInt(value.slice(4, 6), 10);
  const day = parseInt(value.slice(6, 8), 10);
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

const isBirthValid = computed(() => isValidBirthDate(formData.value.emp_birth));

const isFormValid = computed(() => {
  return !!(
    formData.value.emp_name &&
    formData.value.emp_birth &&
    isBirthValid.value &&
    formData.value.emp_job &&
    formData.value.emp_in_date
  );
});

// watch
watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    birthNumberFront.value = '';
    birthNumberBack.value = '';
    if (props.employee) {
      // 수정 모드
      formData.value = { 
        ...props.employee, 
        emp_birth_number: '' 
      };
      // 생년월일에서 앞자리 추출
      birthNumberFront.value = extractBirthFront(props.employee.emp_birth);
    } else {
      // 신규 등록 모드
      formData.value = {
        emp_code: '',
        m_sisul_code: user.value?.m_sisul_code || '',
        emp_name: '',
        emp_birth: '',
        emp_birth_number: '',
        emp_job: '',
        emp_in_date: '',
        emp_out_date: '',
        emp_phone: '',
        emp_email: '',
        emp_addr: '',
        emp_addr_detail: ''
      };
    }
  }
});

// 메서드
const closeDialog = () => {
  emit('update:modelValue', false);
  birthNumberFront.value = '';
  birthNumberBack.value = '';
  formData.value = {
    emp_code: '',
    m_sisul_code: '',
    emp_name: '',
    emp_birth: '',
    emp_birth_number: '',
    emp_job: '',
    emp_in_date: '',
    emp_out_date: '',
    emp_phone: '',
    emp_email: '',
    emp_addr: '',
    emp_addr_detail: ''
  };
};

const saveEmployee = async () => {
  if (!isFormValid.value) return;
  
  isSaving.value = true;
  
  try {
    // 주민등록번호 조합: 앞자리 + 뒷자리
    const fullBirthNumber = (birthNumberFront.value && birthNumberBack.value) 
      ? (birthNumberFront.value + birthNumberBack.value) 
      : '';
    
    const response = await axios.post('/api/calc/employee', {
      ...formData.value,
      emp_birth_number: fullBirthNumber,
      m_sisul_code: user.value?.m_sisul_code
    }, {
      withCredentials: true
    });
    
    if (response.data.success) {
      // 수정 모드에서 입사일이 변경된 경우, leb_member_info 테이블도 업데이트
      if (editingEmployee.value && formData.value.emp_in_date !== props.employee?.emp_in_date) {
        try {
          await axios.post('/api/emp/update-start-date', {
            m_sisul_code: user.value?.m_sisul_code,
            emp_name: formData.value.emp_name,
            emp_birth: formData.value.emp_birth,
            emp_in_date: formData.value.emp_in_date
          }, {
            withCredentials: true
          });
        } catch (startDateError) {
          console.warn('급여 테이블 입사일 업데이트 실패:', startDateError);
        }
      }
      
      let message = editingEmployee.value ? '사원 정보가 수정되었습니다.' : '사원이 등록되었습니다.';
      
      // 신규 등록 시 생성된 사원코드 표시
      if (!editingEmployee.value && response.data.empCode) {
        message += ` (사원코드: ${response.data.empCode})`;
      }
      
      store.commit('showSnackbar', {
        text: message,
        color: 'success'
      });
      
      emit('saved');
      closeDialog();
    } else {
      store.commit('showSnackbar', {
        text: response.data.message || '저장에 실패했습니다.',
        color: 'error'
      });
    }
  } catch (error: any) {
    console.error('사원 저장 오류:', error);
    store.commit('showSnackbar', {
      text: error.response?.data?.message || '사원 저장 중 오류가 발생했습니다.',
      color: 'error'
    });
  } finally {
    isSaving.value = false;
  }
};

// 입력 포맷팅
const onNameInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const value = target.value.replace(/[^가-힣]/g, '');
  formData.value.emp_name = value;
};

const onBirthInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const value = target.value.replace(/[^\d]/g, '').slice(0, 8);
  formData.value.emp_birth = value;
};

const onPhoneInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const digits = target.value.replace(/[^\d]/g, '').slice(0, 11);
  
  let formatted = digits;
  if (digits.length > 3 && digits.length <= 7) {
    formatted = `${digits.slice(0,3)}-${digits.slice(3)}`;
  } else if (digits.length > 7) {
    formatted = `${digits.slice(0,3)}-${digits.slice(3,7)}-${digits.slice(7)}`;
  }
  
  formData.value.emp_phone = formatted;
};

const onBirthNumberFrontInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const value = target.value.replace(/[^\d]/g, '');
  birthNumberFront.value = value;
};

const onBirthNumberBackInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const value = target.value.replace(/[^\d]/g, '');
  birthNumberBack.value = value;
};

// 주소 검색
const loadDaumPostcodeScript = async (): Promise<void> => {
  if ((window as any).daum?.Postcode) return;
  if (daumScriptLoading) {
    await daumScriptLoading;
    return;
  }
  daumScriptLoading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Daum Postcode script load failed'));
    document.head.appendChild(script);
  });
  await daumScriptLoading;
};

const embedDaumPostcode = async () => {
  addressLoading.value = true;
  try {
    await loadDaumPostcodeScript();
    await nextTick();
    const container = postcodeContainer.value;
    if (!container) throw new Error('Container not ready');
    container.innerHTML = '';
    const Postcode = (window as any).daum?.Postcode;
    if (!Postcode) throw new Error('Daum Postcode is undefined');
    const postcode = new Postcode({
      oncomplete: (data: any) => {
        const road = data.roadAddress;
        const jibun = data.jibunAddress;
        const selected = road && road.length > 0 ? road : jibun;
        
        formData.value.emp_addr = selected || '';
        
        addressDialog.value = false;
        addressLoading.value = false;
      },
      onresize: (size: any) => {
        try {
          (container as HTMLDivElement).style.height = `${size.height}px`;
        } catch {}
      },
      width: '100%',
      height: '100%'
    });
    try {
      postcode.embed(container, { autoClose: true });
      addressLoading.value = false;
    } catch (e) {
      addressLoading.value = false;
      throw e;
    }
  } catch (e) {
    addressLoading.value = false;
    throw e;
  }
};

const openAddressModal = async () => {
  addressDialog.value = true;
};

watch(addressDialog, async (val) => {
  if (val) {
    try {
      await embedDaumPostcode();
    } catch (e) {
      addressDialog.value = false;
      store.commit('showSnackbar', {
        text: '주소 검색 로드에 실패했습니다.',
        color: 'error'
      });
    }
  }
});
</script>

<style lang="scss">
@import '@/assets/styles/fonts.scss';

// v-dialog는 teleport되므로 전역 스타일 필요
.employee-dialog-wrapper,
.address-dialog-wrapper {
  font-family: $Noto_Sans_KR !important;
  
  * {
    font-family: $Noto_Sans_KR !important;
  }
}

// scoped 스타일로 나머지 적용
.employee-card {
  font-family: $Noto_Sans_KR !important;
  
  // Vuetify 컴포넌트들에 폰트 적용
  :deep(.v-card),
  :deep(.v-btn),
  :deep(.v-text-field),
  :deep(.v-select),
  :deep(.v-chip),
  :deep(.v-table),
  :deep(.v-file-input),
  :deep(.v-checkbox) {
    font-family: $Noto_Sans_KR !important;
    
    * {
      font-family: $Noto_Sans_KR !important;
    }
  }

  .employee-dialog-header {
    background: #1e3a8a;
    color: white;
    font-weight: 600;
    padding: 16px 20px;
  }

  .employee-dialog-content {
    padding: 24px;
    max-height: 600px;
    overflow-y: auto;
  }

  .form-section {
    margin-bottom: 24px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  .section-title {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid #e5e7eb;
  }

  .compact-field {
    margin-bottom: 16px;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  .compact-label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
    
    .required-text {
      font-size: 11px;
      font-weight: 600;
      color: #dc2626;
      margin-left: 6px;
    }
  }

  .compact-input {
    width: 100%;
    padding: 10px 12px;
    font-size: 14px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    transition: all 0.15s ease;
    
    &:focus {
      outline: none;
      border-color: #1e3a8a;
      box-shadow: 0 0 0 3px rgba(30, 58, 138, 0.1);
    }
    
    &::placeholder {
      color: #9ca3af;
    }
  }

  .field-hint {
    margin-top: 4px;
    font-size: 12px;
    color: #6b7280;
  }

  .field-error {
    margin-top: 4px;
    font-size: 12px;
    color: #dc2626;
  }

  .address-input-group {
    display: flex;
    gap: 8px;
  }
  
  .birth-number-input-group {
    display: flex;
    align-items: center;
    gap: 4px;
    
    .birth-front {
      width: 100px;
      text-align: center;
      font-weight: 600;
      background-color: #f9fafb;
    }
    
    .birth-separator {
      font-size: 18px;
      font-weight: 500;
      color: #374151;
      padding: 0 4px;
    }
    
    .birth-back {
      flex: 1;
      max-width: 140px;
    }
  }

  .address-search-btn {
    padding: 0 16px;
    height: 38px;
    font-size: 13px;
    font-weight: 600;
    color: #ffffff;
    background: #1e3a8a;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
    flex-shrink: 0;
    
    &:hover {
      background: #1e40af;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(30, 58, 138, 0.3);
    }
    
    &:active {
      transform: translateY(0);
    }
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.95);
    z-index: 10;
  }

  .loading-text {
    font-size: 14px;
    color: #666;
    text-align: center;
    margin-top: 12px;
  }
}

.address-card {
  font-family: $Noto_Sans_KR !important;
  
  :deep(.v-card),
  :deep(.v-btn),
  :deep(.v-progress-circular) {
    font-family: $Noto_Sans_KR !important;
    
    * {
      font-family: $Noto_Sans_KR !important;
    }
  }
}
</style>

